import { useEffect, useMemo } from "react";
import { useAtom } from "lib/global-context";
import { goalAmountAtom } from "lib/atoms";
import { Slider } from "components/ui/slider-with-tooltip";
import { formatCurrency } from "lib/utils";
import styles from "./goal-slider.module.css";

interface GoalSliderProps {
  minGoal: number;
  maxGoal: number;
  defaultValue: number;
  headline: string;
  span?: string;
}

const GoalSlider = ({
  minGoal,
  maxGoal,
  defaultValue,
  headline,
  span,
}: GoalSliderProps) => {
  const [amount, setAmount] = useAtom(goalAmountAtom);

  const { lowerMidpoint, midpoint, upperMidpoint } = useMemo(() => {
    const midpoint = (minGoal + maxGoal) / 2;
    const lowerMidpoint = (minGoal + midpoint) / 2;
    const upperMidpoint = (midpoint + maxGoal) / 2;
    return { lowerMidpoint, midpoint, upperMidpoint };
  }, [minGoal, maxGoal]);

  useEffect(() => {
    setAmount(defaultValue);
  }, [defaultValue]);

  const handleSliderChange = (value: number[]) => {
    setAmount(value[0]);
  };

  return (
    <div className={styles.container}>
      <h3 className={styles.headline}>{headline}</h3>
      <div className={styles.sliderContainer}>
        <div className={styles.sliderWrapper}>
          <Slider
            value={[amount ?? defaultValue]}
            min={minGoal}
            max={maxGoal}
            step={5000}
            onValueChange={handleSliderChange}
          />
        </div>
        <div className={styles.labels}>
          <div>
            <span>{formatCurrency(minGoal)}</span>
          </div>
          <div className={styles.hiddenLabel}>
            <span>{formatCurrency(lowerMidpoint)}</span>
          </div>
          <div className={styles.centerLabel}>
            <span className={styles.boldLabel}>{formatCurrency(midpoint)}</span>
          </div>
          <div className={styles.hiddenLabel}>
            <span>{formatCurrency(upperMidpoint)}</span>
          </div>
          <div className={styles.endLabel}>
            <span>{formatCurrency(maxGoal)}</span>
          </div>
        </div>
      </div>
      {span && <div className={styles.span}>{span}</div>}
    </div>
  );
};

export default GoalSlider;
